// src/pages/MetricsResults.tsx

/**
 * Metrics and results page for the Quantik Mind enterprise frontend.
 *
 * This page is responsible for:
 * - listing paginated Quantum runs for the current project
 * - expanding run details on demand
 * - displaying KPIs for each completed run
 * - visualizing the Quantum graph for a selected run
 * - presenting the selected experiments and their selection rationale
 *
 * The page combines summary-level run navigation with drill-down inspection of
 * detailed selection data returned by the backend.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SavedNearMissExplanation, { PagedNearMissExplanation } from '../components/results/SavedNearMissExplanation';
import FunctionalRunTests from '../components/results/FunctionalRunTests';
import { comparePriority } from '../features/results/utils/priority';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Link as RouterLink,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import PageHeader from '../components/PageHeader';
import { trackFrontendEvent } from '../api/events';
import QuantumGraph from '../components/QuantumGraph';
import Button from '../components/common/ActionButton';
import { useProject } from '../context/ProjectContext';
import { type BusinessRiskMetrics, type RunSummary } from '../api/runs';
import {
  deriveBusinessRiskMetrics,
  formatPercent,
} from '../utils/businessRiskMetrics';
import {
  COLUMN_TOOLTIPS,
  getCompactReason,
  getDetailedReason,
  getLogicalService,
  getPrincipleLabel,
  KPI_TOOLTIPS,
} from '../features/results/utils/resultsFormatting';
import { useRunExpansion } from '../features/results/hooks/useRunExpansion';
import { useRunsPage } from '../features/results/hooks/useRunsPage';

const TooltipLabel = ({
  label,
  tooltip,
}: {
  label: string;
  tooltip?: string;
}) => (
  <Tooltip title={tooltip ?? ''} arrow disableHoverListener={!tooltip}>
    <Box
      component="span"
      tabIndex={tooltip ? 0 : undefined}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 0,
      }}
    >
      {label}
      {tooltip && <InfoOutlinedIcon sx={{ fontSize: 14, opacity: 0.7 }} />}
    </Box>
  </Tooltip>
);

const RiskMetricTile = ({
  label,
  value,
  helper,
  tooltip,
}: {
  label: string;
  value: string;
  helper?: string;
  tooltip?: string;
}) => (
  <Tooltip title={tooltip ?? ''} arrow disableHoverListener={!tooltip}>
    <Paper
      tabIndex={tooltip ? 0 : undefined}
      sx={{
        p: 1.6,
        bgcolor: 'rgba(255,255,255,0.035)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        minWidth: 0,
        height: '100%',
        boxShadow: 'none',
      }}
    >
      <Stack spacing={0.8} sx={{ height: '100%', minHeight: 132 }}>
        <Stack direction="row" spacing={0.6} alignItems="flex-start" sx={{ minHeight: 34 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#7D8590',
              textTransform: 'uppercase',
              fontWeight: 700,
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {label}
          </Typography>
          {tooltip && (
            <InfoOutlinedIcon sx={{ fontSize: 14, color: '#7D8590', mt: 0.15, flexShrink: 0 }} />
          )}
        </Stack>
        <Typography variant="h6" sx={{ color: '#E6EDF3', fontWeight: 760, lineHeight: 1.15 }}>
          {value}
        </Typography>
        {helper && (
          <Typography variant="caption" sx={{ color: '#7D8590', lineHeight: 1.45, mt: 'auto' }}>
            {helper}
          </Typography>
        )}
      </Stack>
    </Paper>
  </Tooltip>
);

const RiskCoverageSummary = ({ metrics }: { metrics: BusinessRiskMetrics }) => {
  const mapped = metrics.mapped_risk_coverage;
  const rows = [
    { key: 'overall', title: 'Overall risk coverage', color: '#39ff14' },
    { key: 'critical', title: 'Critical risk coverage', color: '#F43F5E' },
    { key: 'high', title: 'High risk coverage', color: '#F87171' },
    { key: 'medium', title: 'Medium risk coverage', color: '#FBBF24' },
    { key: 'low', title: 'Low risk coverage', color: '#C9D1D9' },
  ] as const;
  const tooltip = 'Calculated from this run’s saved test-to-risk mappings. Tests have not necessarily run. This does not establish complete behavioral coverage or elimination of risk.';
  const CoverageRow = ({ keyName, title, color }: { keyName: keyof NonNullable<BusinessRiskMetrics['mapped_risk_coverage']>; title: string; color: string }) => {
    const value = mapped?.[keyName];
    const pct = typeof value?.covered_pct === 'number' ? value.covered_pct : null;
    const gaps = value ? value.total_count - value.covered_count : null;
    return <Stack spacing={0.7} sx={{ minWidth: 0, width: '100%' }}>
      <Tooltip title={tooltip} arrow><Typography variant="caption" sx={{ color: '#E6EDF3', fontWeight: 700, width: 'fit-content' }}>{title}</Typography></Tooltip>
      {value && value.total_count > 0 ? <>
        <Box sx={{ display: 'flex', width: '100%', height: 10, borderRadius: 99, overflow: 'hidden' }}>
          <Box sx={{ width: `${pct ?? 0}%`, bgcolor: color }} />
          <Box sx={{ flexGrow: 1, bgcolor: 'rgba(148, 163, 184, 0.36)' }} />
        </Box>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          <Typography variant="caption" sx={{ color }}>{formatPercent(pct)} · {value.covered_count}/{value.total_count}</Typography>
          <Typography variant="caption" sx={{ color: gaps ? '#F87171' : '#7D8590' }}>{gaps} without a selected test</Typography>
        </Stack>
      </> : <Typography variant="caption" sx={{ color: '#7D8590' }}>No risks defined</Typography>}
    </Stack>;
  };

  return (
    <Paper
      sx={{
        p: 2,
        bgcolor: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 2,
        boxShadow: 'none',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      <Stack spacing={1.2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          gap={0.75}
          sx={{ minWidth: 0 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Mapped risk coverage
          </Typography>
          <Typography variant="caption" sx={{ color: '#7D8590' }}>
            {mapped?.overall ? `${mapped.overall.covered_count}/${mapped.overall.total_count} defined risks` : 'Unavailable'}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: '#AAB4C3' }}>
          Coverage shows defined risks with at least one associated test selected.
        </Typography>
        {rows.map(({ key, title, color }) => (
          <CoverageRow key={key} keyName={key} title={title} color={color} />
        ))}
        {!!mapped?.unknown?.total_count && <Alert severity="warning">{mapped.unknown.total_count} risk mappings have missing or conflicting severity and are reported separately.</Alert>}
      </Stack>
    </Paper>
  );
};

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
};

const SELECTED_TESTS_PAGE_SIZE = 50;

const getExperimentKey = (runId: string, experiment: any, index: number) => {
  const derivedKey = [
    experiment.service,
    experiment.experiment_type,
    experiment.origin,
    experiment.reason,
  ]
    .filter(Boolean)
    .join('|');
  const stableId =
    [experiment.id, experiment.experiment_id, experiment.test_id, experiment.name, derivedKey]
      .find((candidate) => candidate !== undefined && candidate !== null && candidate !== '') ??
    index;

  return `${runId}:${stableId}`;
};

const getRenderedDetailedReason = (experiment: any) => {
  const detailedReason = getDetailedReason(experiment);

  if (typeof detailedReason === 'string') {
    return detailedReason;
  }

  if (typeof detailedReason === 'object') {
    return JSON.stringify(detailedReason, null, 2);
  }

  return String(detailedReason);
};

const formatRunLaunchTime = (run: RunSummary) => {
  const launchTimestamp = run.queued_at ?? run.started_at ?? run.created_at;
  const launchTime = new Date(launchTimestamp);

  if (Number.isNaN(launchTime.getTime())) return 'N/A';

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(launchTime);
};

const SelectedExperimentRow = React.memo(
  ({
    experiment,
    experimentKey,
    isExpanded,
    runType,
    onToggle,
  }: {
    experiment: any;
    experimentKey: string;
    isExpanded: boolean;
    runType: 'chaos' | 'functional';
    onToggle: (experimentKey: string) => void;
  }) => {
    const compactReason = useMemo(
      () => getCompactReason(experiment, runType),
      [experiment, runType]
    );
    const principleLabel = useMemo(() => getPrincipleLabel(experiment), [experiment]);
    const logicalService = useMemo(
      () => getLogicalService(experiment.service),
      [experiment.service]
    );
    const detailedReason = useMemo(
      () =>
        isExpanded
          ? experiment.__cachedDetailedReason ?? getRenderedDetailedReason(experiment)
          : null,
      [experiment, isExpanded]
    );

    const handleToggle = useCallback(() => {
      onToggle(experimentKey);
    }, [experimentKey, onToggle]);

    return (
      <React.Fragment>
        <TableRow hover onClick={handleToggle} sx={{ cursor: 'pointer' }}>
          <TableCell align="center" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
            {(experiment.experiment_type ?? experiment.type) ?? '-'}
          </TableCell>

          <TableCell align="center" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
            <Stack spacing={0.3}>
              <Typography fontWeight={600}>{logicalService}</Typography>

              {experiment.service !== logicalService && (
                <Typography fontSize="0.75rem" color="#7D8590">
                  {experiment.service}
                </Typography>
              )}
            </Stack>
          </TableCell>

          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>{experiment.priority_score == null ? 'Unavailable' : `${experiment.priority_rank}. ${experiment.priority_score.toFixed(3)}`}</TableCell>

          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>{experiment.informative_probability == null ? 'Unavailable' : `${(experiment.informative_probability * 100).toFixed(1)}%`}</TableCell>

          <TableCell
            sx={{
              color: '#539BF5',
              fontWeight: 600,
              textAlign: 'left',
              pl: 2,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  flexGrow: 1,
                  textAlign: 'left',
                  fontSize: '0.92rem',
                  lineHeight: 1.4,
                }}
              >
                {compactReason}
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    ml: 1,
                    color: '#7D8590',
                    fontSize: '0.75rem',
                  }}
                >
                  {principleLabel}
                </Box>
              </Typography>

              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggle();
                }}
                sx={{
                  color: isExpanded ? '#eba747' : '#555',
                  transition: 'transform 0.2s ease',
                  transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)',
                  ml: 1,
                }}
              >
                {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
              </IconButton>
            </Box>
          </TableCell>
        </TableRow>

        {isExpanded && (
          <TableRow data-testid={`why-selected-panel-${experimentKey}`}>
            <TableCell
              colSpan={5}
              sx={{
                background: 'rgba(0, 0, 0, 0.3)',
                color: '#C9D1D9',
                fontSize: '0.9rem',
                p: 2,
                whiteSpace: 'pre-line',
                lineHeight: 1.7,
              }}
            >
              {detailedReason}
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  }
);

const SelectedExperimentsTable = React.memo(
  ({
    runId,
    runType,
    selectedExperiments,
    filter,
    sortConfig,
    onSort,
  }: {
    runId: string;
    runType: 'chaos' | 'functional';
    selectedExperiments: any[];
    filter: string;
    sortConfig: SortConfig;
    onSort: (key: string) => void;
  }) => {
    const [expandedExperimentKey, setExpandedExperimentKey] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const reasonCacheRef = useRef<Record<string, string>>({});

    const filteredSortedExperiments = useMemo(() => {
      const search = filter.trim().toLowerCase();
      const filtered = search
        ? selectedExperiments.filter((experiment) => {
            return (
              (experiment.experiment_type ?? '').toLowerCase().includes(search) ||
              (experiment.service ?? '').toLowerCase().includes(search) ||
              (experiment.origin ?? '').toLowerCase().includes(search) ||
              (experiment.reason ?? '').toLowerCase().includes(search)
            );
          })
        : selectedExperiments;

      const sorted = [...filtered];
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      const key = sortConfig.key;

      sorted.sort((a, b) => {
        if (key === 'priority_score') return comparePriority(a, b, sortConfig.direction === 'desc');
        if (key === 'informative_probability') {
          const left = a.informative_probability;
          const right = b.informative_probability;
          if (left == null || right == null) return left == null ? (right == null ? 0 : 1) : -1;
          return (left - right) * direction;
        }
        if (key === 'principle') {
          return getPrincipleLabel(a).localeCompare(getPrincipleLabel(b)) * direction;
        }

        const valueA = (a as any)[key] ?? '';
        const valueB = (b as any)[key] ?? '';

        return String(valueA).localeCompare(String(valueB)) * direction;
      });

      return sorted;
    }, [filter, selectedExperiments, sortConfig.direction, sortConfig.key]);

    const totalPages = Math.max(
      1,
      Math.ceil(filteredSortedExperiments.length / SELECTED_TESTS_PAGE_SIZE)
    );
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * SELECTED_TESTS_PAGE_SIZE;
    const pageEnd = Math.min(
      pageStart + SELECTED_TESTS_PAGE_SIZE,
      filteredSortedExperiments.length
    );
    const visibleExperiments = useMemo(
      () => filteredSortedExperiments.slice(pageStart, pageEnd),
      [filteredSortedExperiments, pageEnd, pageStart]
    );

    useEffect(() => {
      setPage(1);
      setExpandedExperimentKey(null);
    }, [filter, runId, sortConfig.direction, sortConfig.key]);

    const toggleExperiment = useCallback(
      (experimentKey: string) => {
        setExpandedExperimentKey((currentKey) =>
          currentKey === experimentKey ? null : experimentKey
        );
      },
      []
    );

    const getCachedReasonExperiment = useCallback((experiment: any, experimentKey: string) => {
      if (!reasonCacheRef.current[experimentKey]) {
        reasonCacheRef.current[experimentKey] = getRenderedDetailedReason(experiment);
      }

      return {
        ...experiment,
        __cachedDetailedReason: reasonCacheRef.current[experimentKey],
      };
    }, []);

    const columns = useMemo(
      () => [
        {
          key: 'experiment_type',
          label: runType === 'functional' ? 'Test Case' : 'Experiment',
        },
        { key: 'service', label: 'Service' },
        {
          key: 'priority_score',
          label: 'Priority',
          tooltip: 'Saved default priority. In model mode it follows estimated informative-evidence probability; fallback runs use an explicitly heuristic score.',
        },
        {
          key: 'informative_probability',
          label: 'Estimated informative evidence',
          tooltip: 'Estimated probability under the saved functional model. Unmodeled and historical results remain unavailable.',
        },
        {
          key: 'reason',
          label: 'Why Selected',
          tooltip: COLUMN_TOOLTIPS.why_selected,
        },
      ],
      [runType]
    );

    return (
      <Stack spacing={1.5}>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 500,
            width: '100%',
            maxWidth: '100%',
            overflowY: 'auto',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            minWidth: 0,
          }}
        >
          <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {columns.map(({ key, label, tooltip }) => (
                  <TableCell
                    key={key}
                    onClick={() => onSort(key)}
                    sx={{
                      fontWeight: 600,
                      cursor: 'pointer',
                      color: '#7D8590',
                      textAlign: key === 'reason' ? 'left' : 'center',
                      width:
                        key === 'experiment_type'
                          ? '22%'
                          : key === 'service'
                          ? '20%'
                          : key === 'priority_score'
                          ? '12%'
                          : key === 'informative_probability'
                          ? '16%'
                          : '30%',
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <TableSortLabel
                      active={sortConfig.key === key}
                      direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
                      sx={{
                        color: '#7D8590 !important',
                      }}
                    >
                      <TooltipLabel label={label} tooltip={tooltip} />
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleExperiments.map((experiment, index) => {
                const absoluteIndex = pageStart + index;
                const experimentKey = getExperimentKey(runId, experiment, absoluteIndex);
                const isExpanded = expandedExperimentKey === experimentKey;
                const rowExperiment = isExpanded
                  ? getCachedReasonExperiment(experiment, experimentKey)
                  : experiment;

                return (
                  <SelectedExperimentRow
                    key={experimentKey}
                    experiment={rowExperiment}
                    experimentKey={experimentKey}
                    isExpanded={isExpanded}
                    runType={runType}
                    onToggle={toggleExperiment}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredSortedExperiments.length > SELECTED_TESTS_PAGE_SIZE && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="caption" sx={{ color: '#7D8590' }}>
              Showing {pageStart + 1}-{pageEnd} of {filteredSortedExperiments.length} selected tests
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={safePage <= 1}
                onClick={() => {
                  setExpandedExperimentKey(null);
                  setPage((currentPage) => Math.max(1, currentPage - 1));
                }}
              >
                Previous
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={safePage >= totalPages}
                onClick={() => {
                  setExpandedExperimentKey(null);
                  setPage((currentPage) => Math.min(totalPages, currentPage + 1));
                }}
              >
                Next
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  }
);

/**
 * Render the metrics and results page.
 *
 * @returns Metrics and results page element.
 */
const SHOW_CHAOS_TAB = false;

const MetricsResultsPage = () => {
  const { currentProject } = useProject();
  const location = useLocation();
  const { runId } = useParams<{ runId: string }>();
  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [filter, setFilter] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({
    key: 'priority_score',
    direction: 'desc',
  });
  const [runTypeFilter, setRunTypeFilter] = useState<'chaos' | 'functional'>(
    'functional'
  );
  const [includeImportedHistory, setIncludeImportedHistory] = useState(false);
  const {
    totalRuns,
    runs,
    loading,
    error,
    loadRuns,
  } = useRunsPage({
    projectId: currentProject?.id,
    page,
    pageSize,
    runType: runTypeFilter,
    includeImportedHistory,
  });
  const {
    expandedRuns,
    visibleExpandedRuns,
    loadingRunIds,
    runErrors,
    retryRunDetail,
    showGraphRunId,
    setShowGraphRunId,
    toggleExpand,
  } = useRunExpansion({
    runs,
    projectId: currentProject?.id,
    runId,
    searchParams,
  });

  const trackedResultsViewRef = useRef(false);

  useEffect(() => {
    const defaultTab = (location.state as any)?.defaultTab as
      | 'chaos'
      | 'functional'
      | undefined;

    if (defaultTab) {
      setRunTypeFilter(defaultTab);
    }
  }, [location.state]);

  useEffect(() => {
    if (!currentProject?.id) {
      return;
    }

    setPage(1);
  }, [currentProject?.id]);

  useEffect(() => {
    if (!currentProject?.id || trackedResultsViewRef.current) {
      return;
    }

    trackedResultsViewRef.current = true;
    void trackFrontendEvent('selection_result_viewed', {
      project_id: currentProject.id,
      run_id: searchParams.get('runId') || runId || null,
      run_type: runTypeFilter,
    }).catch(() => undefined);
  }, [currentProject?.id, runId, runTypeFilter, searchParams]);

  /**
   * Update experiment table sorting configuration.
   *
   * @param key - Column key used for sorting.
   */
  const handleSort = useCallback((key: string) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  if (loading) {
    return (
      <Box>
        <PageHeader
          icon={<AssessmentIcon fontSize="inherit" />}
          title="Metrics & Results"
          subtitle="Loading..."
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader
          icon={<AssessmentIcon fontSize="inherit" />}
          title="Metrics & Results"
        />
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <PageHeader
        icon={<BarChartIcon fontSize="inherit" />}
        title="Metrics & Results"
        subtitle="View and analyze quantum selection performance"
      />

      <Paper
        sx={{
          mb: 3,
          bgcolor: '#161B22',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
        }}
      >
        <Tabs
          value={runTypeFilter}
          onChange={(_, value) => setRunTypeFilter(value)}
          sx={{
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              minHeight: 48,
              color: '#7D8590',
              '&.Mui-selected': { color: '#E6EDF3' },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#39ff14',
              height: 2,
            },
          }}
        >
          <Tab value="functional" label="Functional Tests" />
          {SHOW_CHAOS_TAB && <Tab value="chaos" label="Chaos Engineering" />}
        </Tabs>
      </Paper>

      {runTypeFilter === 'functional' && (
        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <Switch
              checked={includeImportedHistory}
              onChange={(_, checked) => {
                setIncludeImportedHistory(checked);
                setPage(1);
              }}
            />
          }
          label="Including imported execution history"
        />
      )}

      <TableContainer
        component={Paper}
        sx={{
          bgcolor: '#161B22',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                '& th': {
                  color: '#7D8590',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  py: 1.5,
                  px: { xs: 0.5, sm: 1 },
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                },
              }}
            >
              <TableCell align="center" sx={{ width: '23%' }}>Date</TableCell>
              <TableCell align="center">
                <TooltipLabel label="Selected / Total" tooltip={COLUMN_TOOLTIPS.selected_total} />
              </TableCell>
              <TableCell align="center">
                <TooltipLabel label="Test reduction" tooltip="Percentage of tests not selected from this saved run." />
              </TableCell>
              <TableCell align="center">
                <TooltipLabel label="Run type" tooltip="Functional or chaos selection run." />
              </TableCell>
              <TableCell align="center">
                <TooltipLabel label="Run ID" tooltip="Stable identifier for the saved run." />
              </TableCell>
              <TableCell align="center" sx={{ width: '13%' }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {runs.map((run) => {
              const importedHistory =
                run.run_source === 'seed' ||
                run.history_kind === 'imported_execution_history';
              const isExpanded = visibleExpandedRuns.has(run.run_id);
              const details = expandedRuns[run.run_id];

              const pctBar =
                run.total_experiments > 0
                  ? Math.round(
                      (run.selected_experiments / run.total_experiments) * 100
                    )
                  : 0;

              const selectedExps =
                details?.experiments?.filter((experiment) => experiment.selected) ?? [];
              const businessMetrics = deriveBusinessRiskMetrics(details);
              const skippedSummary = details?.skippedTestsSummary;
              const alwaysGreenAvoided =
                typeof details?.avoided_always_green === 'number'
                  ? details.avoided_always_green
                  : typeof details?.business_metrics?.always_green_tests_skipped === 'number'
                  ? details.business_metrics.always_green_tests_skipped
                  : typeof skippedSummary?.always_green_avoided_count === 'number'
                  ? skippedSummary.always_green_avoided_count
                  : undefined;
              const redundantTestsAvoided =
                typeof skippedSummary?.redundant_tests_avoided_count === 'number'
                  ? skippedSummary.redundant_tests_avoided_count
                  : typeof details?.redundant_tests_avoided === 'number'
                  ? details.redundant_tests_avoided
                  : typeof details?.redundancy_saved === 'number'
                  ? details.redundancy_saved
                  : undefined;
              const summaryMetrics: BusinessRiskMetrics = {
                risk_coverage: run.risk_coverage,
                residual_risk:
                  typeof run.risk_coverage === 'number'
                    ? Math.max(0, 100 - run.risk_coverage)
                    : undefined,
                top_risk_coverage:
                  typeof run.top_risk_coverage === 'number'
                    ? run.top_risk_coverage
                    : undefined,
                high_risk_coverage:
                  typeof run.high_risk_coverage === 'number'
                    ? run.high_risk_coverage
                    : undefined,
                tests_reduced: run.reduction_percentage ?? undefined,
                test_budget: run.test_coverage,
                risk_efficiency: run.risk_efficiency,
              };

              return (
                <React.Fragment key={run.run_id}>
                  <TableRow id={`run-${run.run_id}`}>
                    <TableCell align="center" sx={{ px: { xs: 0.5, sm: 1 }, overflowWrap: 'anywhere' }}>
                      {formatRunLaunchTime(run)}
                    </TableCell>

                    <TableCell align="center" sx={{ px: { xs: 0.5, sm: 1 } }}>
                      {importedHistory ? (
                        <Typography variant="body2">
                          Imported execution history · {run.total_experiments} recorded results
                        </Typography>
                      ) : (
                        <>
                          {run.selected_experiments} / {run.total_experiments}
                          <LinearProgress
                            variant="determinate"
                            value={pctBar}
                            sx={{ height: 6, borderRadius: 4, mt: 0.5 }}
                          />
                        </>
                      )}
                    </TableCell>

                    <TableCell align="center" sx={{ px: { xs: 0.5, sm: 1 } }}>
                      {importedHistory ? 'N/A' : formatPercent(summaryMetrics.tests_reduced)}
                    </TableCell>

                    <TableCell align="center" sx={{ px: { xs: 0.5, sm: 1 } }}>
                      {run.run_type}
                    </TableCell>

                    <TableCell align="center" sx={{ px: { xs: 0.5, sm: 1 } }}>
                      <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>{run.run_id}</Typography>
                    </TableCell>

                    <TableCell align="center" sx={{ px: { xs: 0.25, sm: 0.75 } }}>
                      <Button
                        onClick={() => toggleExpand(run.run_id)}
                        size="small"
                        sx={{ minWidth: 0, maxWidth: '100%', px: { xs: 0.5, sm: 1 } }}
                        startIcon={
                          isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                        }
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, border: 0, minWidth: 0 }}>
                      <Collapse in={isExpanded}>
                        <Box sx={{ p: { xs: 1.5, sm: 3 }, minWidth: 0, bgcolor: 'rgba(0, 0, 0, 0.3)' }}>
                          {details && importedHistory ? (
                            <Stack spacing={1}>
                              <Typography variant="h6">Imported execution history</Typography>
                              <Typography variant="body2" color="text.secondary">
                                This episode records imported executions; it is not a selector decision. Informativeness probability, test reduction and near-miss comparison are not applicable.
                              </Typography>
                              <Typography variant="body2">
                                Persisted results: {details.total_experiments}
                              </Typography>
                            </Stack>
                          ) : details ? (
                            <Stack spacing={3} sx={{ minWidth: 0, maxWidth: '100%' }}>
                              <Stack spacing={1.5}>
                                <Stack
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
                                    gap: 1.5,
                                    width: '100%',
                                    minWidth: 0,
                                  }}
                                >
                                  <RiskMetricTile
                                    label="Test reduction"
                                    value={formatPercent(businessMetrics.tests_reduced)}
                                    helper={`${details.selected_experiments} of ${details.total_experiments} selected`}
                                    tooltip="Percentage of tests skipped from the original test set. This measures execution reduction, not risk elimination."
                                  />
                                  <RiskMetricTile
                                    label="Highest estimated informativeness"
                                    value={formatPercent(typeof businessMetrics.highest_informative_probability === 'number' ? businessMetrics.highest_informative_probability * 100 : null)}
                                    helper="Saved model estimate"
                                    tooltip="Highest model-estimated chance that a selected test provides new, relevant risk evidence. This is a per-test maximum, not a probability for the whole selection, and uses the complete frozen selected set."
                                  />
                                  <RiskMetricTile
                                    label="Risks without selected tests"
                                    value={`${businessMetrics.coverage_gaps?.length ?? 'N/A'}`}
                                    helper="Defined risks with no associated selected test"
                                    tooltip="This is mapped set coverage, not observed evidence or residual business risk."
                                  />
                                  <RiskMetricTile
                                    label="Critical risk coverage"
                                    value={formatPercent(businessMetrics.mapped_risk_coverage?.critical?.covered_pct)}
                                    helper={`${businessMetrics.mapped_risk_coverage?.critical?.covered_count ?? 'N/A'} / ${businessMetrics.mapped_risk_coverage?.critical?.total_count ?? 'N/A'} defined critical risks`}
                                    tooltip="Defined critical risks with at least one associated selected test. This is mapped coverage, not proof that tests ran or risk was eliminated."
                                  />
                                  <RiskMetricTile
                                    label="Overall risk coverage"
                                    value={formatPercent(businessMetrics.mapped_risk_coverage?.overall?.covered_pct)}
                                    helper={`${businessMetrics.mapped_risk_coverage?.overall?.covered_count ?? 'N/A'} / ${businessMetrics.mapped_risk_coverage?.overall?.total_count ?? 'N/A'} defined risks`}
                                    tooltip="Distinct defined risks with at least one associated selected test. Overlapping mappings are counted once."
                                  />
                                </Stack>

                                <RiskCoverageSummary metrics={businessMetrics} />

                                <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontWeight: 700 } }}>
                                  <Box component="summary">Risks without selected tests</Box>
                                  <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                                    {(['critical', 'high', 'medium', 'low', 'unknown'] as const).map(level => <Typography key={level} variant="caption"><strong>{level[0].toUpperCase() + level.slice(1)}:</strong> {businessMetrics.coverage_gap_counts?.[level] ?? 0}</Typography>)}
                                  </Stack>
                                  <Stack spacing={1} sx={{ mt: 1 }}>
                                    {(businessMetrics.coverage_gaps ?? []).map((gap) => (
                                      <Box component="details" key={gap.proposition_id} sx={{ pl: 1 }}>
                                        <Box component="summary">{gap.proposition_name ?? gap.proposition_id} · {gap.criticality} · {gap.associated_test_count} associated tests</Box>
                                        <Typography variant="caption" display="block" sx={{ color: '#7D8590', overflowWrap: 'anywhere' }}>Technical ID: {gap.proposition_id}</Typography>
                                        <Typography variant="caption" display="block" sx={{ color: '#AAB4C3' }}>No associated test was selected. Highest saved estimate: {formatPercent(typeof gap.highest_informative_probability === 'number' ? gap.highest_informative_probability * 100 : null)}; decision reason: {(gap.exclusion_reason ?? 'Unavailable').replace(/_/g, ' ')}.</Typography>
                                        {(gap.associated_tests ?? []).map(test => <Typography key={test.test_id ?? test.test_identifier} variant="caption" display="block" sx={{ color: '#AAB4C3', overflowWrap: 'anywhere' }}>{test.test_identifier ?? `Test ${test.test_id}`} · {test.service ?? 'service unavailable'} · {formatPercent(typeof test.informative_probability === 'number' ? test.informative_probability * 100 : null)} · {(test.inclusion_rule ?? 'reason unavailable').replace(/_/g, ' ')}</Typography>)}
                                      </Box>
                                    ))}
                                  </Stack>
                                </Box>

                              </Stack>

                              {details.detail_view === 'summary' ? <FunctionalRunTests runId={details.run_id} projectId={details.project_id} selectedTotal={details.selected_experiments} /> : <Stack spacing={1}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  Breakdown
                                </Typography>

                                <TextField
                                  label="Filter experiments"
                                  size="small"
                                  value={filter}
                                  onChange={(event) =>
                                    setFilter(event.target.value)
                                  }
                                  sx={{ maxWidth: 320 }}
                                />

                                {selectedExps.length > 0 ? (
                                  <SelectedExperimentsTable
                                    runId={run.run_id}
                                    runType={run.run_type}
                                    selectedExperiments={selectedExps}
                                    filter={filter}
                                    sortConfig={sortConfig}
                                    onSort={handleSort}
                                  />
                                ) : (
                                  <Typography fontStyle="italic">
                                    No selected experiments to display.
                                  </Typography>
                                )}
                              </Stack>}

                              {run.run_type === 'functional' && (details.detail_view === 'summary'
                                ? <PagedNearMissExplanation explanation={details.near_miss_explanation} runId={details.run_id} projectId={details.project_id} />
                                : <SavedNearMissExplanation explanation={details.near_miss_explanation} runId={details.run_id} projectId={details.project_id} />)}

                              <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontWeight: 700 } }}>
                                <Box component="summary">Technical diagnostics and model details</Box>
                                <Stack spacing={1.25} sx={{ mt: 1 }}>
                                  <Typography variant="body2"><strong>Minimum informativeness for standard selection:</strong> {formatPercent(typeof businessMetrics.membership_reconciliation?.effective_threshold === 'number' ? businessMetrics.membership_reconciliation.effective_threshold * 100 : null)}. Tests meeting this estimated probability are selected. Additional tests may be included to cover important risks.</Typography>
                                  <Typography variant="body2"><strong>Selection breadth:</strong> {businessMetrics.selection_configuration?.effective_tau ?? businessMetrics.membership_reconciliation?.tau ?? 'Unavailable'}. Lower values make selection more restrictive. Coverage requirements can add tests. The value does not guarantee a selection percentage.</Typography>
                                  <Typography variant="caption" sx={{ color: '#7D8590' }}>Metric contract: {businessMetrics.metric_semantics_version ?? 'historical/unversioned'}. Mapped coverage is recomputed from this run&apos;s frozen rows.</Typography>
                                  {businessMetrics.model_details && <Box component="details"><Box component="summary">Model details</Box>
                                    <Typography variant="body2">{businessMetrics.model_details.warning ?? 'Model provenance warning unavailable.'}</Typography>
                                    <Typography variant="caption" display="block">Model: {businessMetrics.model_details.model_version ?? 'Unavailable'}</Typography>
                                    <Typography variant="caption" display="block">Predictor: {businessMetrics.model_details.probability_source ?? 'Unavailable'}; training data: {businessMetrics.model_details.training_data_kind ?? 'Unavailable'}.</Typography>
                                    <Typography variant="caption" display="block">Validation/calibration: raw model probabilities are served under the saved predictor-selection protocol; real-world calibration has not been demonstrated.</Typography>
                                    <Typography variant="caption" display="block">Technical status: {businessMetrics.model_details.calibration_status ?? 'Unavailable'}</Typography>
                                  </Box>}
                                  <Box component="details"><Box component="summary">Legacy audit diagnostics</Box>
                                    <Typography variant="caption" display="block">{businessMetrics.legacy_score_diagnostics?.score_semantics ?? 'Legacy score diagnostics unavailable.'}</Typography>
                                    <Typography variant="caption" display="block">Selected p_initial score mass: {formatPercent(businessMetrics.legacy_score_diagnostics?.selected_score_mass_pct)}. This is not mapped coverage or model probability.</Typography>
                                    <Typography variant="caption" display="block">Dependency flags on selected tests: {businessMetrics.classification_diagnostics?.legacy_dependency_signal_selected_count ?? 'N/A'}; name-similarity flags on unselected tests: {businessMetrics.classification_diagnostics?.name_similarity_unselected_count ?? redundantTestsAvoided ?? 'N/A'}; historically-stable flags on unselected tests: {businessMetrics.classification_diagnostics?.historically_stable_unselected_count ?? 'N/A'}; pre-model always-green aggregate: {businessMetrics.classification_diagnostics?.legacy_pre_model_always_green_count ?? alwaysGreenAvoided ?? 'N/A'}.</Typography>
                                  </Box>
                                  {showGraphRunId === run.run_id ? <Box><QuantumGraph run={{ ...details, selectedExperiments: selectedExps } as any} projectId={details.project_id} /><Button size="small" onClick={() => setShowGraphRunId(null)}>Hide graph</Button></Box> : <Button size="small" variant="outlined" onClick={() => setShowGraphRunId(run.run_id)}>Visualize graphically</Button>}
                                </Stack>
                              </Box>

                              <Paper variant="outlined" sx={{ alignSelf: 'stretch', p: 2, bgcolor: 'rgba(34, 197, 94, 0.08)', borderColor: 'rgba(34, 197, 94, 0.45)', borderRadius: 2 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" useFlexGap flexWrap="wrap">
                                  <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
                                    <Typography variant="subtitle2" sx={{ color: '#E6EDF3', fontWeight: 800 }}>Why were tests skipped?</Typography>
                                    <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.4 }}>Review saved non-selection reasons and diagnostic classifications.</Typography>
                                  </Box>
                                  <Button component={RouterLink} to={`/runs/${run.run_id}/skipped-tests`} variant="outlined" size="small" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, borderColor: 'rgba(34, 197, 94, 0.8)', color: '#22C55E', textTransform: 'none', fontWeight: 700, whiteSpace: 'normal', overflowWrap: 'anywhere', maxWidth: '100%', '&:hover': { borderColor: '#22C55E', bgcolor: 'rgba(34, 197, 94, 0.12)' } }}>Open skipped tests analysis</Button>
                                </Stack>
                              </Paper>
                            </Stack>
                          ) : loadingRunIds.has(run.run_id) ? (
                            <Stack spacing={2} alignItems="center" py={4}>
                              <CircularProgress
                                size={32}
                                sx={{ color: '#00B4FF' }}
                              />
                              <Typography color="#7D8590">
                                Loading run details...
                              </Typography>
                            </Stack>
                          ) : runErrors[run.run_id] ? <Alert severity="error">{runErrors[run.run_id]} <Button onClick={() => retryRunDetail(run.run_id)}>Retry saved run</Button></Alert> : null}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {totalRuns > pageSize && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(totalRuns / pageSize)}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#E6EDF3',
                '&.Mui-selected': {
                  backgroundColor: '#39ff14',
                  color: '#0D1117',
                },
              },
            }}
          />

          <Typography
            sx={{ ml: 3, alignSelf: 'center', color: '#7D8590' }}
          >
            Showing {(page - 1) * pageSize + 1}-
            {Math.min(page * pageSize, totalRuns)} of {totalRuns} runs
          </Typography>
        </Box>
      )}

      {runs.length === 0 && (
        <Typography sx={{ mb: 3, fontStyle: 'italic', color: '#888' }}>
          No Quantum Runs yet – fire up the engine!
        </Typography>
      )}
    </Box>
  );
};

export default MetricsResultsPage;
